import { useState, useMemo } from 'preact/hooks'
import type { TouchPointViewDto } from '@/types/touchPoint'

type GroupByFunction = (touchPoint: TouchPointViewDto) => string

interface UseTouchPointGroupsOptions {
  touchPoints: TouchPointViewDto[]
  groupBy: GroupByFunction
}

export function useTouchPointGroups({ touchPoints, groupBy }: UseTouchPointGroupsOptions) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  // Group touchpoints using the provided groupBy function
  const groupedTouchPoints = useMemo(() => {
    return touchPoints.reduce((acc, tp) => {
      const groupKey = groupBy(tp)
      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(tp)
      return acc
    }, {} as Record<string, TouchPointViewDto[]>)
  }, [touchPoints, groupBy])

  // Toggle group expansion
  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))
  }

  // Check if a group is expanded (default to false for collapsed)
  const isGroupExpanded = (groupKey: string) => {
    return expandedGroups[groupKey] ?? false
  }

  return {
    groupedTouchPoints,
    expandedGroups,
    toggleGroup,
    isGroupExpanded,
  }
}
